/**
 * Map pathname → page_group + page_name. Used to enrich page_viewed and
 * other navigation events without requiring every call site to specify a
 * group manually.
 */

const RULES: Array<{prefix: string; group: string; name?: string}> = [
  // Watch
  {prefix: '/tv-and-vod', group: 'watch'},
  {prefix: '/on-demand', group: 'watch'},
  {prefix: '/watch', group: 'watch'},
  {prefix: '/news', group: 'watch'},
  {prefix: '/live', group: 'watch'},
  {prefix: '/tv-guide', group: 'watch'},
  {prefix: '/shorts', group: 'shorts'},
  // Listen
  {prefix: '/audio-player', group: 'listen'},
  {prefix: '/audio', group: 'listen'},
  {prefix: '/listen', group: 'listen'},
  {prefix: '/radio', group: 'listen'},
  {prefix: '/podcast', group: 'listen'},
  // Learn / Academia
  {prefix: '/academia', group: 'learn'},
  {prefix: '/learn', group: 'learn'},
  {prefix: '/course-unit', group: 'learn'},
  // Matching is exact-or-slash (see derivePageGroup), so '/course' does NOT
  // cover '/course-category' — it needs its own entry. Same for the others
  // added below.
  {prefix: '/course-category', group: 'learn'},
  {prefix: '/course', group: 'learn'},
  {prefix: '/exam-result-overview', group: 'learn'},
  {prefix: '/exam', group: 'learn'},
  {prefix: '/quizz-survey-collection', group: 'learn'},
  {prefix: '/quizz-survey-category', group: 'learn'},
  {prefix: '/player-quizz-survey', group: 'learn'},
  {prefix: '/quizz-survey', group: 'learn'},
  {prefix: '/quiz', group: 'learn'},
  {prefix: '/personality', group: 'learn'},
  {prefix: '/opportunity', group: 'learn'},
  // Constant/routes.ts:59 declares JOB_HUB = '/job-hub' (singular). The rule
  // list previously had only '/job-hubs', which — because matching is
  // exact-or-slash — could never match anything, so every job-hub page landed
  // in 'other'. The plural has been removed rather than kept alongside: it is
  // provably unreachable and keeping it invites the same confusion again.
  {prefix: '/job-hub', group: 'jobs'},
  {prefix: '/job-vacancies', group: 'jobs'},
  {prefix: '/job-training-certificate', group: 'jobs'},
  {prefix: '/single-job', group: 'jobs'},
  {prefix: '/linked-job', group: 'jobs'},
  // Discover / Search
  //
  // '/discovery' — EVIDENCE AND ITS LIMIT, read before changing:
  //   FOR: Amplitude shows a live page_name `discovery/item/:id` — 4,926 views
  //        on 2026-08-04 and 5,746 on 2026-08-05. It stayed flat across the bot
  //        sweep that multiplied scanner paths ~6.7x, so it is real user
  //        traffic, not probe noise. Today it is grouped as 'other'.
  //   AGAINST: no route named 'discovery' exists anywhere in this repository —
  //        not in Constant/routes.ts, not in src/App/Route/**, not in
  //        src/pages/**. The only match is the Umbraco GraphQL field
  //        `discoverySlider`, which is a homepage component, not a route.
  //   So the rule is kept because the traffic is observed, but where it is
  //   served from is unresolved and worth chasing (Umbraco-generated path? an
  //   older deploy?). The rule is inert if the path never occurs.
  {prefix: '/discovery', group: 'discover'},
  {prefix: '/discover', group: 'discover'},
  {prefix: '/search', group: 'search'},
  // Account / Subscription
  {prefix: '/my-profile', group: 'profile'},
  {prefix: '/profile', group: 'profile'},
  {prefix: '/account', group: 'account'},
  {prefix: '/checkout', group: 'subscription'},
  {prefix: '/subscription', group: 'subscription'},
  {prefix: '/paywall', group: 'subscription'},
  // Other sections
  {prefix: '/awards', group: 'awards'},
  {prefix: '/partner-landing', group: 'partners'},
  {prefix: '/partner', group: 'partners'},
  {prefix: '/partners', group: 'partners'},
  {prefix: '/artist-services', group: 'partners'},
  {prefix: '/bookmarks', group: 'profile'},
  {prefix: '/more', group: 'more'},
];

// UNMAPPED — these routes exist in Constant/routes.ts but have no entry above,
// so they still resolve to 'other'. Left deliberately: choosing their group is
// a reporting decision, not an engineering one, and should be agreed with the
// data team before it changes their breakdowns.
//
//   /coming-soon  /entrepreneurial-hub  /forms  /jb  /oc  /promocode
//   /terms-and-conditions  /thank-you  /thank-you-modal  /tutorial
//
// Note this is also why 'other' must never be treated as junk and filtered out
// wholesale — it contains real pages.

export function derivePageGroup(pathname: string): string {
  // strip locale prefix (/fr/, /en/, /pt/)
  const stripped = pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
  for (const r of RULES) {
    if (stripped === r.prefix || stripped.startsWith(r.prefix + '/')) {
      return r.group;
    }
  }
  if (stripped === '/' || stripped === '') return 'home';
  return 'other';
}

export function derivePageName(pathname: string): string {
  const stripped = pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
  if (stripped === '/' || stripped === '') return 'home';
  // collapse dynamic segments (long ids) to ":id"
  return stripped
    .split('/')
    .filter(Boolean)
    .map(seg => (seg.length > 20 || /^[0-9a-f-]{8,}$/i.test(seg) ? ':id' : seg))
    .join('/');
}
